import React, { useState, useCallback, useMemo, useRef } from 'react'

import type { FormikProps } from 'formik'

import { Text } from '@chakra-ui/react'
import {
  Stack,
  Flex,
  Grid,
  GridItem,
  Form,
  Select,
  Button,
  Spinner,
  Box,
  Skeleton,
  EmptyState,
  Typography,
  useToast,
} from '@pnld/components-web'

import {
  useDashboardPredictionYears,
  useDashboardPrediction,
  useDashboardPredictionEducationStages,
  useDashboardPanoramaOverview,
  useDashboardPanoramaStates,
} from '@/api/dashboard/queries'
import {
  DashboardPredictionPayload,
  DashboardPredictionEducationStagesPayload,
  DashboardPanoramaPayload,
  PanoramaStateItem,
} from '@/api/dashboard/types'
import { PieChart, BarChart } from '@/components/Charts'
import SlidesDisplay from '@/components/Display/Slides'
import TotalizerDisplay from '@/components/Display/Totalizer'
import Map from '@/components/Map'
import { States } from '@/components/Map/types'
import {
  regionOptions,
  regionEnumToStringMap,
  stateEnumToSiglaMap,
} from '@/data/dashboard'
import { PredictionYear, CensusYear, State } from '@/types/dashboard'
import { getErrorMessage } from '@/utils/api'

import {
  mockPredictionYears,
  mockPrediction,
  mockEducationStages,
  mockOverview,
  mockStates,
} from './mock'
import { PanoramaFilterValues } from './types'

const USE_MOCK = false

const siglaToStateEnumMap = Object.fromEntries(
  Object.entries(stateEnumToSiglaMap).map(([enumVal, sigla]) => [
    sigla,
    Number(enumVal) as State,
  ])
) as Record<States, State>

const formatNumber = (num: number) => num.toLocaleString('pt-BR')

const dependencyDescriptionMap: Record<string, string> = {
  Municipal: 'Escolas municipais',
  Estadual: 'Escolas estaduais',
  Federal: 'Escolas federais',
  Privada: 'Escolas privadas',
}

const locationDescriptionMap: Record<string, string> = {
  Urbana: 'Escolas urbanas',
  Rural: 'Escolas rurais',
}

const educationStageDescriptionMap: Record<string, string> = {
  'Educação Infantil': 'Educação Infantil',
  'Ensino Fundamental - Anos Iniciais': 'Anos iniciais',
  'Ensino Fundamental - Anos Finais': 'Anos finais',
  'Ensino Fundamental': 'Ensino Fundamental',
  'Ensino Médio': 'Ensino Médio',
  'Ensino Médio - Técnico': 'Ensino Médio Técnico',
  EJA: 'EJA',
  Técnico: 'Técnico',
}

const getDescription = (map: Record<string, string>, label: string) =>
  map[label] ?? label

const Panorama: React.FC = () => {
  const toast = useToast()
  const formRef = useRef<FormikProps<PanoramaFilterValues> | null>(null)
  const selectRefs = useRef<Record<string, any>>({})

  const [filterValues, setFilterValues] = useState<PanoramaFilterValues | null>(
    null
  )
  const [selectedStates, setSelectedStates] = useState<States[]>([])
  const [hoveredStateSigla, setHoveredStateSigla] = useState<string | null>(
    null
  )
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  const initialValues: PanoramaFilterValues = {
    year: '',
    region: '',
  }

  const errorHandler = useCallback(
    (error: unknown) => {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
    },
    [toast]
  )

  const { data: predictionYearsApi, isLoading: isPredictionYearsLoadingApi } =
    useDashboardPredictionYears({ onError: errorHandler, enabled: !USE_MOCK })

  const predictionYears = USE_MOCK ? mockPredictionYears : predictionYearsApi
  const isPredictionYearsLoading = USE_MOCK
    ? false
    : isPredictionYearsLoadingApi

  const predictionYearsOptions = useMemo(
    () =>
      (predictionYears || []).map(year => ({
        label: year.label,
        value: `${year.predictionYear}#${year.censusYear}`,
      })),
    [predictionYears]
  )

  const buildPanoramaPayload = useCallback(
    (values: PanoramaFilterValues): DashboardPanoramaPayload | null => {
      if (!values.year) return null

      const [predictionYear, baseCensusYear] = values.year.split('#')
      if (!predictionYear || !baseCensusYear) return null

      const coUf =
        selectedStates.length > 0
          ? selectedStates.map(sigla => siglaToStateEnumMap[sigla])
          : undefined

      return {
        nuPredictionYear: Number(predictionYear) as PredictionYear,
        nuCensusYear: Number(baseCensusYear) as CensusYear,
        coRegion: values.region || undefined,
        coUf,
      }
    },
    [selectedStates]
  )

  const buildPredictionPayload = useCallback(
    (values: PanoramaFilterValues): DashboardPredictionPayload | null => {
      if (!values.year) return null

      const [predictionYear, baseCensusYear] = values.year.split('#')
      if (!predictionYear || !baseCensusYear) return null

      const coUf =
        selectedStates.length > 0
          ? selectedStates.map(sigla => siglaToStateEnumMap[sigla])
          : undefined

      return {
        nuPredictionYear: [Number(predictionYear)] as PredictionYear[],
        nuCensusYear: [Number(baseCensusYear)] as CensusYear[],
        coRegion: values.region || undefined,
        coUf,
      }
    },
    [selectedStates]
  )

  const panoramaPayload = useMemo(
    () => (filterValues ? buildPanoramaPayload(filterValues) : null),
    [filterValues, buildPanoramaPayload]
  )
  const predictionPayload = useMemo(
    () => (filterValues ? buildPredictionPayload(filterValues) : null),
    [filterValues, buildPredictionPayload]
  )

  const { data: overviewApi, isLoading: isOverviewLoadingApi } =
    useDashboardPanoramaOverview(
      panoramaPayload || ({} as DashboardPanoramaPayload),
      { onError: errorHandler, enabled: !USE_MOCK && !!panoramaPayload }
    )

  const overview = USE_MOCK && filterValues ? mockOverview : overviewApi
  const isOverviewLoading = USE_MOCK
    ? false
    : isOverviewLoadingApi && !!panoramaPayload

  const { data: statesDataApi } = useDashboardPanoramaStates(
    panoramaPayload || ({} as DashboardPanoramaPayload),
    { onError: errorHandler, enabled: !USE_MOCK && !!panoramaPayload }
  )

  const statesData = USE_MOCK && filterValues ? mockStates : statesDataApi

  const { data: predictionApi, isLoading: isPredictionLoadingApi } =
    useDashboardPrediction(
      predictionPayload || ({} as DashboardPredictionPayload),
      { onError: errorHandler, enabled: !USE_MOCK && !!predictionPayload }
    )

  const prediction = USE_MOCK && filterValues ? mockPrediction : predictionApi
  const isPredictionLoading = USE_MOCK
    ? false
    : isPredictionLoadingApi && !!predictionPayload

  const { data: educationStagesApi, isLoading: isEducationStagesLoadingApi } =
    useDashboardPredictionEducationStages(
      predictionPayload
        ? {
            nuPredictionYear: predictionPayload.nuPredictionYear,
            nuCensusYear: predictionPayload.nuCensusYear,
            coRegion: predictionPayload.coRegion,
            coUf: predictionPayload.coUf,
          }
        : ({} as DashboardPredictionEducationStagesPayload),
      { enabled: !USE_MOCK && !!predictionPayload }
    )

  const predictionEducationStages =
    USE_MOCK && filterValues ? mockEducationStages : educationStagesApi
  const isEducationStagesLoading = USE_MOCK
    ? false
    : isEducationStagesLoadingApi

  const predictionData = prediction?.[0]
  const isLoading = isOverviewLoading || isPredictionLoading
  const hasData = overview && predictionData

  const stateDataMap = useMemo(() => {
    if (!statesData?.states) return {}
    const map: Record<string, PanoramaStateItem> = {}
    statesData.states.forEach(state => {
      map[state.sgUf] = state
    })
    return map
  }, [statesData])

  const handleOnSubmit = useCallback(
    (values: PanoramaFilterValues) => {
      if (!values.year) {
        toast({
          status: 'warning',
          title: 'Selecione um ano de predição.',
        })
        return
      }
      setFilterValues(values)
    },
    [toast]
  )

  const handleOnFilter = useCallback(() => {
    if (formRef.current) {
      const values = formRef.current.values
      if (!values.year) {
        toast({
          status: 'warning',
          title: 'Selecione um ano de predição.',
        })
        return
      }
      setFilterValues({ ...values })
    }
  }, [toast])

  const handleOnClear = useCallback(() => {
    formRef.current?.resetForm()
    Object.values(selectRefs.current).forEach((ref: any) => {
      ref?.clearValue?.()
    })
    setFilterValues(null)
    setSelectedStates([])
  }, [])

  const handleStateClick = useCallback((stateSigla: string) => {
    setSelectedStates(prev => {
      const sigla = stateSigla as States
      if (prev.includes(sigla)) {
        return prev.filter(s => s !== sigla)
      }
      return [...prev, sigla]
    })
  }, [])

  const handleStateMouseEnter = useCallback((stateCode: string) => {
    setHoveredStateSigla(stateCode)
  }, [])

  const handleStateMouseLeave = useCallback(() => {
    setHoveredStateSigla(null)
  }, [])

  const handleMapMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (hoveredStateSigla) {
        setTooltipPosition({ x: event.clientX, y: event.clientY })
      }
    },
    [hoveredStateSigla]
  )

  const mapRegion = useMemo(() => {
    if (filterValues?.region) {
      return regionEnumToStringMap[filterValues.region]
    }
    return undefined
  }, [filterValues?.region])

  const hoveredStateData = hoveredStateSigla
    ? stateDataMap[hoveredStateSigla]
    : null

  return (
    <Stack gap={6}>
      <Stack
        gap={4}
        bg="white"
        p={4}
        borderRadius="md"
        boxShadow="sm"
        sx={{ form: { width: '100%' } }}
      >
        <Form
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
        >
          <Stack gap={3}>
            <Flex gap={4}>
              <Box flex={1}>
                <Select
                  selectRef={ref => (selectRefs.current.year = ref)}
                  name="year"
                  label="Ano"
                  options={predictionYearsOptions}
                  placeholder="Selecione o ano"
                  isLoading={isPredictionYearsLoading}
                  disabled={isPredictionYearsLoading}
                />
              </Box>
              <Box flex={1}>
                <Select
                  selectRef={ref => (selectRefs.current.region = ref)}
                  name="region"
                  label="Região"
                  options={regionOptions}
                  placeholder="Todas"
                />
              </Box>
            </Flex>
            <Text fontSize="xs" color="brand.neutral.dark_1">
              *<b>Ano base</b>: Ano do censo utilizado para geração da predição.
            </Text>
            <Flex justify="flex-end" gap={2}>
              <Button
                iconName="filter-circle-xmark"
                variant="highlight"
                label="Limpar filtro"
                border="square"
                onClick={handleOnClear}
              />
              <Button
                iconName="filter"
                label="Filtrar"
                border="square"
                onClick={handleOnFilter}
              />
            </Flex>
          </Stack>
        </Form>
      </Stack>

      {isLoading && (
        <Stack alignItems="center" justifyContent="center">
          <Spinner size="xl" />
        </Stack>
      )}

      {!isLoading && hasData && (
        <Stack gap={6}>
          <Flex gap={6}>
            <Flex
              flex={2}
              align="center"
              justify="center"
              direction="column"
              position="relative"
              onMouseMove={handleMapMouseMove}
            >
              <Map
                region={mapRegion}
                selectedStates={selectedStates}
                isClickable
                isHoverable
                onStateClick={handleStateClick}
                onStateMouseEnter={handleStateMouseEnter}
                onStateMouseLeave={handleStateMouseLeave}
              />
              <Typography variant="b-sm" color="brand.neutral.dark_2" mt={2}>
                Clique no mapa para mais informações
              </Typography>

              {hoveredStateData && (
                <Box
                  position="fixed"
                  top={`${tooltipPosition.y - 10}px`}
                  left={`${tooltipPosition.x + 15}px`}
                  bg="white"
                  boxShadow="lg"
                  borderRadius="md"
                  px={4}
                  py={6}
                  zIndex={1000}
                  pointerEvents="none"
                  border="1px solid"
                  borderColor="gray.200"
                  minWidth="280px"
                  maxWidth="320px"
                >
                  <Stack spacing={0}>
                    <Typography variant="h-2xs">
                      {hoveredStateData.noUf} ({hoveredStateData.sgUf})
                    </Typography>
                    <Text fontSize="10px" color="brand.neutral.dark_2">
                      Total de alunos preditos
                    </Text>
                  </Stack>
                  <Box borderTop="1px solid" borderColor="gray.200" my={3} />
                  <Stack spacing={0} mb={3} align="center">
                    <Text
                      fontSize="13px"
                      fontWeight="bold"
                      color="brand.highlight.dark"
                    >
                      {formatNumber(hoveredStateData.predictionStudents)}
                    </Text>
                    <Text fontSize="10px" color="brand.neutral.dark_2">
                      No estado
                    </Text>
                  </Stack>
                  {hoveredStateData.dependency &&
                    hoveredStateData.dependency.length > 0 && (
                      <>
                        <Box
                          borderTop="1px solid"
                          borderColor="gray.200"
                          my={3}
                        />
                        <Stack spacing={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="brand.primary.dark_2"
                          >
                            Por Dependência
                          </Text>
                          {hoveredStateData.dependency.map(d => (
                            <Flex key={d.label} gap={2} align="baseline">
                              <Text
                                fontSize="13px"
                                fontWeight="bold"
                                whiteSpace="nowrap"
                              >
                                {formatNumber(d.value)}
                              </Text>
                              <Text fontSize="10px">
                                {getDescription(
                                  dependencyDescriptionMap,
                                  d.label
                                )}
                              </Text>
                            </Flex>
                          ))}
                        </Stack>
                      </>
                    )}
                  {hoveredStateData.location &&
                    hoveredStateData.location.length > 0 && (
                      <>
                        <Box
                          borderTop="1px solid"
                          borderColor="gray.200"
                          my={3}
                        />
                        <Stack spacing={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="brand.primary.dark_2"
                          >
                            Por Localização
                          </Text>
                          {hoveredStateData.location.map(l => (
                            <Flex key={l.label} gap={2} align="baseline">
                              <Text
                                fontSize="13px"
                                fontWeight="bold"
                                whiteSpace="nowrap"
                              >
                                {formatNumber(l.value)}
                              </Text>
                              <Text fontSize="10px">
                                {getDescription(
                                  locationDescriptionMap,
                                  l.label
                                )}
                              </Text>
                            </Flex>
                          ))}
                        </Stack>
                      </>
                    )}
                  {hoveredStateData.educationStage &&
                    hoveredStateData.educationStage.length > 0 && (
                      <>
                        <Box
                          borderTop="1px solid"
                          borderColor="gray.200"
                          my={3}
                        />
                        <Stack spacing={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="brand.primary.dark_2"
                          >
                            Por Etapa de ensino
                          </Text>
                          {hoveredStateData.educationStage.map(e => (
                            <Flex key={e.label} gap={2} align="baseline">
                              <Text
                                fontSize="13px"
                                fontWeight="bold"
                                whiteSpace="nowrap"
                              >
                                {formatNumber(e.value)}
                              </Text>
                              <Text fontSize="10px">
                                {getDescription(
                                  educationStageDescriptionMap,
                                  e.label
                                )}
                              </Text>
                            </Flex>
                          ))}
                        </Stack>
                      </>
                    )}
                </Box>
              )}
            </Flex>
            <Stack flex={1} gap={4} justify="center">
              <TotalizerDisplay
                number={overview.predictionStudents}
                label="Total de alunos preditos"
                variant="highlight"
                icon="user-graduate"
                fullWidth
              />
              <TotalizerDisplay
                number={overview.censusClasses}
                label="Total de turmas presentes no Censo base"
                icon="users"
                fullWidth
              />
              <TotalizerDisplay
                number={overview.censusSchools}
                label="Total de escolas presentes no Censo base"
                icon="school"
                fullWidth
              />
            </Stack>
          </Flex>

          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <GridItem>
              <PieChart
                data={predictionData.dependency.map(d => ({
                  name: d.label,
                  value: d.value,
                }))}
                centralLabel={
                  <Stack spacing={0}>
                    <Typography variant="h-2xs">
                      Alunos por
                      <br />
                      Dependência
                    </Typography>
                    <Typography color="brand.highlight.dark" variant="h-2xs">
                      {predictionData.nuCensusYear}
                    </Typography>
                  </Stack>
                }
                legendDirection="column"
                rightGutter={300}
              />
            </GridItem>
            <GridItem>
              <PieChart
                data={predictionData.location.map(l => ({
                  name: l.label,
                  value: l.value,
                }))}
                centralLabel={
                  <Stack spacing={0}>
                    <Typography variant="h-2xs">
                      Alunos por
                      <br />
                      Localização
                    </Typography>
                    <Typography color="brand.highlight.dark" variant="h-2xs">
                      {predictionData.nuCensusYear}
                    </Typography>
                  </Stack>
                }
                legendDirection="column"
                rightGutter={300}
              />
            </GridItem>
          </Grid>

          <Stack
            padding={4}
            gap={4}
            borderRadius="md"
            border="2px solid"
            borderColor="gray.200"
          >
            <TotalizerDisplay
              number={overview.predictionStudents}
              label="Total de alunos preditos"
              variant="highlight"
              icon="user-graduate"
              fullWidth
              small
            />

            {isEducationStagesLoading ? (
              <Skeleton height={360} borderRadius="md" />
            ) : predictionEducationStages &&
              predictionEducationStages.length > 0 &&
              predictionEducationStages[0].educationData.length > 0 ? (
              <SlidesDisplay
                items={predictionEducationStages[0].educationData.map(
                  stage => ({
                    title: stage.dsEducationStage,
                    content: (
                      <Box height={300}>
                        <BarChart
                          data={stage.yearGrades.map(grade => ({
                            name: grade.label,
                            value: grade.value,
                          }))}
                        />
                      </Box>
                    ),
                  })
                )}
              />
            ) : (
              <EmptyState
                title="Nenhum dado encontrado"
                description="Não há dados de etapas de ensino para os filtros selecionados."
                iconProps={{ name: 'table' }}
                shaped="false"
              />
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default Panorama
